// v3.18-1332 — Pipeline "Last Activity" cell rendering helper.
// v3.18-1333 — Compact relative-time display; actor + activity moved
// off the row into the hover tooltip so Last Activity consumes minimal
// row space (2m · 35m · 2h · Yesterday · 4d · No Activity).
//
// The backend attaches a fully-computed `last_activity` object to every
// pipeline row (single batch query, no N+1) so we render directly from
// the payload with zero extra HTTP calls or client-side stitching.
//
// Shape:
//   {
//     timestamp:            ISO 8601 UTC
//     relative_time:        (unused for display — v3.18-1333 recomputes
//                           a compact label from `timestamp`)
//     user_name:            actor label (for tooltip only)
//     activity_type:        "call" | "sms" | "email" | "voicemail" |
//                           "note" | "ghost" | "appointment" | ...
//     activity_description: human sentence for tooltip
//     color:                "green" | "yellow" | "orange" | "red"
//   }

export const LAST_ACTIVITY_COLOR_CLASS = {
  green:  'text-emerald-700',
  yellow: 'text-amber-600',
  orange: 'text-orange-600',
  red:    'text-red-600',
};

// v3.18-1333 — Color sparingly.  Only stale deals (orange/red) draw
// the eye; fresh activity is neutral so the row stays quiet.
export const LAST_ACTIVITY_BADGE_CLASS = {
  green:  'text-slate-500',
  yellow: 'text-slate-500',
  orange: 'text-orange-600 font-semibold',
  red:    'text-red-600 font-semibold',
};

/** Formats an ISO timestamp as "Aug 12, 2026  2:43 PM EDT" for tooltips. */
export function formatExactTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
    const timePart = d.toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short',
    });
    return `${datePart}  ${timePart}`;
  } catch { return ''; }
}

/** v3.18-1333 — Compact relative label: "2m" / "35m" / "2h" /
 *  "Yesterday" / "4d" / "No Activity". */
export function compactRelativeTime(iso) {
  if (!iso) return 'No Activity';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No Activity';
  const now = Date.now();
  const secs = Math.max(0, Math.floor((now - d.getTime()) / 1000));
  if (secs < 60)   return 'Just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  const days = Math.floor(secs / 86400);
  if (days === 1) return 'Yesterday';
  return `${days}d`;
}

/** Empty-safe getter — returns a "No Activity" default when the row
 *  has no activity object at all. */
export function lastActivityFor(client) {
  const raw = client?.last_activity;
  if (!raw || typeof raw !== 'object') {
    return {
      timestamp:             null,
      relative_time:         'No Activity',
      user_name:              'No Activity',
      activity_type:         'none',
      activity_description:  'No Activity',
      color:                 'red',
    };
  }
  return {
    timestamp:            raw.timestamp || null,
    relative_time:        raw.relative_time || 'No Activity',
    user_name:            raw.user_name || 'System',
    activity_type:        raw.activity_type || 'none',
    activity_description: raw.activity_description || raw.relative_time || '',
    color:                raw.color || 'red',
  };
}
